import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Avatar,
  VStack,
  HStack,
  Heading,
  IconButton,
  Tooltip,
  Progress,
  Grid,
  Divider,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight, FiArrowLeft } from "react-icons/fi";
import { format, startOfWeek, addWeeks, addDays, isSameDay, parseISO, differenceInDays, isBefore, isAfter } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { IconWrapper } from "../../components/IconWrapper";

const MotionBox = motion(Box);

interface Task {
  id: string;
  name: string;
  plannedStart: string;
  plannedEnd: string;
  progress: number;
  status: string;
  assignedUsers?: string[];
  groupName?: string;
  taskGroupId?: string;
  [key: string]: any;
}

interface ResourceViewProps {
  tasks: any[]; // Task Groups
  users: any[];
  onBack: () => void;
  isDark: boolean;
}

const ResourceView: React.FC<ResourceViewProps> = ({ tasks: taskGroups, users, onBack, isDark }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Flatten all tasks and filter out those without dates or users
  const allTasks = useMemo(() => {
    const flat: Task[] = [];
    taskGroups.forEach((group) => {
      if (group.data && Array.isArray(group.data)) {
        group.data.forEach((task: any) => {
          if (task.plannedStart && task.plannedEnd) {
            flat.push({ ...task, taskGroupId: group.id, groupName: group.title });
          }
          if (task.subtasks && Array.isArray(task.subtasks)) {
            task.subtasks.forEach((sub: any) => {
              if (sub.plannedStart && sub.plannedEnd) {
                flat.push({ ...sub, taskGroupId: group.id, groupName: group.title });
              }
            });
          }
        });
      }
    });
    return flat;
  }, [taskGroups]);

  // Group tasks by user
  const userTaskMap = useMemo(() => {
    const map: Record<string, Task[]> = {};
    users.forEach((user) => {
      map[user.id] = allTasks.filter((task) => 
        task.assignedUsers?.includes(user.id) || 
        (Array.isArray(task.assignedUsers) && task.assignedUsers.includes(user.id)) ||
        (typeof task.assignedUsers === "string" && (task.assignedUsers as string).includes(user.id))
      );
    });
    return map;
  }, [users, allTasks]);

  // Timeline Configuration
  const weekCount = 5;
  const daysToShow = weekCount * 7;
  
  // Filter tasks that are actually in the current view range
  const userTasksInView = useMemo(() => {
    const viewEnd = addDays(currentWeekStart, daysToShow);
    const map: Record<string, Task[]> = {};
    
    users.forEach(user => {
      const allUserTasks = userTaskMap[user.id] || [];
      map[user.id] = allUserTasks.filter(task => {
        try {
          const taskStart = parseISO(task.plannedStart);
          const taskEnd = parseISO(task.plannedEnd);
          return !(isAfter(taskStart, viewEnd) || isBefore(taskEnd, currentWeekStart));
        } catch (e) {
          return false;
        }
      });
    });
    return map;
  }, [userTaskMap, currentWeekStart, daysToShow, users]);

  // ONLY show users who have tasks in the current view
  const usersWithTasks = useMemo(() => {
    return users.filter(user => (userTasksInView[user.id] || []).length > 0);
  }, [users, userTasksInView]);

  // Helper to calculate overlap levels for tasks in a row
  const getTaskLevelsForUser = (userTasks: Task[]) => {
    const sorted = [...userTasks]
      .filter(t => t.plannedStart && t.plannedEnd)
      .sort((a, b) => parseISO(a.plannedStart).getTime() - parseISO(b.plannedStart).getTime());
    
    const levels: string[][] = [];
    const taskLevelMap: Record<string, number> = {};

    sorted.forEach(t => {
      let levelIndex = -1;
      for (let i = 0; i < levels.length; i++) {
        const lastTaskId = levels[i][levels[i].length - 1];
        const lastTask = sorted.find(st => st.id === lastTaskId)!;
        if (parseISO(t.plannedStart) > parseISO(lastTask.plannedEnd)) {
          levelIndex = i;
          break;
        }
      }
      
      if (levelIndex === -1) {
        levels.push([t.id]);
        taskLevelMap[t.id] = levels.length - 1;
      } else {
        levels[levelIndex].push(t.id);
        taskLevelMap[t.id] = levelIndex;
      }
    });

    return { taskLevelMap, totalLevels: levels.length };
  };

  const nextWeeks = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  const prevWeeks = () => setCurrentWeekStart(addWeeks(currentWeekStart, -1));

  const getTaskColor = (task: Task) => {
    switch (task.status?.toLowerCase()) {
      case "completed":
        return { bg: "#34D399", accent: "#10B981" }; // Vibrant Green
      case "in-progress":
        return { bg: "#38BDF8", accent: "#0EA5E9" }; // Light Blue
      case "delay":
        return { bg: "#FB7185", accent: "#F43F5E" }; // Rose/Red
      case "at-risk":
        return { bg: "#FBBF24", accent: "#F59E0B" }; // Orange
      default:
        return { bg: "#818CF8", accent: "#6366F1" }; // Purple (Not Started)
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed": return { color: "#10b981", bg: "#ecfdf5", border: "#10b981" };
      case "in-progress": return { color: "#3b82f6", bg: "#eff6ff", border: "#3b82f6" };
      case "delay": return { color: "#ef4444", bg: "#fef2f2", border: "#ef4444" };
      default: return { color: "#6366f1", bg: "#eef2ff", border: "#6366f1" };
    }
  };

  const safeDateFormat = (dateStr: string) => {
    try {
      if (!dateStr) return "N/A";
      const parsedDate = parseISO(dateStr);
      if (isNaN(parsedDate.getTime())) return "N/A";
      return format(parsedDate, "MMM d, yyyy");
    } catch (e) {
      return "N/A";
    }
  };

  return (
    <Flex direction="column" h="full" bg={isDark ? "#14161B" : "gray.50"} borderRadius="xl" overflow="hidden" position="relative">
      {/* Header */}
      <Flex p={4} justify="space-between" align="center" borderBottom="1px" borderColor={isDark ? "#2E2F33" : "gray.200"} bg={isDark ? "#1A1C1F" : "white"}>
        <HStack spacing={4}>
          <IconButton
            aria-label="Back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={onBack}
            color={isDark ? "gray.400" : "gray.600"}
          />
          <VStack align="start" spacing={0}>
            <Heading size="md" fontWeight="bold" color={isDark ? "white" : "gray.800"}>Task TimeLine</Heading>
          </VStack>
        </HStack>
        
        <HStack spacing={0} bg={isDark ? "#2D2E32" : "gray.100"} p={1} borderRadius="lg" border="1px" borderColor={isDark ? "#3F4045" : "gray.200"}>
          <IconButton aria-label="Previous" icon={<IconWrapper icon={FiChevronLeft} size={18} />} size="sm" onClick={prevWeeks} variant="ghost" _hover={{ bg: isDark ? "#3F4045" : "white" }} borderRadius="md" />
          <Text fontSize="xs" fontWeight="bold" px={6} minW="200px" textAlign="center" color={isDark ? "gray.200" : "gray.700"}>
            {format(currentWeekStart, "MMM d")} - {format(addDays(currentWeekStart, daysToShow - 1), "MMM d, yyyy")}
          </Text>
          <IconButton aria-label="Next" icon={<IconWrapper icon={FiChevronRight} size={18} />} size="sm" onClick={nextWeeks} variant="ghost" _hover={{ bg: isDark ? "#3F4045" : "white" }} borderRadius="md" />
        </HStack>
      </Flex>

      {/* Main Container */}
      <Box flex={1} overflow="auto" position="relative" className="custom-scrollbar">
        <Flex w="max-content" minW="100%" minH="100%" bg={isDark ? "#14161B" : "white"}>
          
          {/* User Sidebar */}
          <Box 
            w="300px" 
            borderRight="1px" 
            borderColor={isDark ? "#2E2F33" : "gray.200"} 
            bg={isDark ? "#1A1C1F" : "white"} 
            position="sticky" 
            left={0} 
            zIndex={20}
          >
            {/* Sidebar Header */}
            <Flex 
              h="56px" 
              align="center" 
              px={4} 
              borderBottom="1px" 
              borderColor={isDark ? "#2E2F33" : "gray.200"}
              position="sticky"
              top={0}
              bg={isDark ? "#1A1C1F" : "white"}
              zIndex={30}
            >
              <Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wider">PEOPLE • {usersWithTasks.length}</Text>
            </Flex>

            {/* User Rows */}
            {usersWithTasks.map((user) => {
              const userTasks = userTasksInView[user.id] || [];
              const util = Math.min(100, Math.round((userTasks.length / 5) * 100));
              const { totalLevels } = getTaskLevelsForUser(userTasks);
              const rowHeight = Math.max(80, 10 + (totalLevels * 52));
              
              return (
                <Box key={user.id} px={4} pt={3} pb={2} h={`${rowHeight}px`} borderBottom="1px" borderColor={isDark ? "#2E2F33" : "gray.100"} _hover={{ bg: isDark ? "#25282C" : "gray.50" }}>
                  <Flex align="center" mb={1}>
                    <Avatar size="sm" name={`${user.firstName} ${user.lastName}`} src={user.photoURL} mr={3} />
                    <VStack align="start" spacing={0} overflow="hidden">
                      <Text fontSize="sm" fontWeight="bold" color={isDark ? "white" : "gray.800"} isTruncated>{user.firstName} {user.lastName}</Text>
                      <Text fontSize="11px" color="gray.500" isTruncated>{user.role || "Team Member"}</Text>
                    </VStack>
                  </Flex>
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="10px" color={isDark ? "gray.400" : "gray.600"}>
                      <Box as="span" color={util > 80 ? "red.400" : "green.400"} fontWeight="bold">● {util}% util</Box> • {userTasks.length} tasks
                    </Text>
                  </HStack>
                  <Progress value={util} size="1.5px" colorScheme={util > 80 ? "red" : "green"} borderRadius="full" bg={isDark ? "#2E2F33" : "gray.100"} />
                </Box>
              );
            })}
            {/* Sidebar filler to maintain bg height */}
            <Box flex={1} />
          </Box>

          {/* Timeline Content */}
          <Box flex={1} position="relative" minW={`${daysToShow * 40}px`} bg={isDark ? "#14161B" : "gray.50"}>
            {/* Timeline Header */}
            <Flex position="sticky" top={0} zIndex={10} bg={isDark ? "#1A1C1F" : "white"} h="56px" borderBottom="1px" borderColor={isDark ? "#2E2F33" : "gray.200"}>
              {Array.from({ length: weekCount }).map((_, i) => (
                <Box key={i} w="280px" borderRight="1px" borderColor={isDark ? "#2E2F33" : "gray.200"}>
                  <Flex h="24px" align="center" justify="center" borderBottom="1px" borderColor={isDark ? "#2E2F33" : "gray.200"}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500">Week of {format(addWeeks(currentWeekStart, i), "MMM d")}</Text>
                  </Flex>
                  <Flex h="31px">
                    {Array.from({ length: 7 }).map((_, j) => {
                      const date = addDays(addWeeks(currentWeekStart, i), j);
                      const isToday = isSameDay(date, new Date());
                      return (
                        <VStack key={j} w="40px" spacing={0} justify="center" bg={isToday ? (isDark ? "#2A2D33" : "blue.50") : "transparent"}>
                          <Text fontSize="8px" color="gray.500" mt="2px">{format(date, "EEEEE")}</Text>
                          <Text fontSize="10px" fontWeight="bold" color={isToday ? "blue.500" : (isDark ? "gray.300" : "gray.700")}>{format(date, "d")}</Text>
                        </VStack>
                      );
                    })}
                  </Flex>
                </Box>
              ))}
            </Flex>

            {/* Timeline Grid Background */}
            <Box position="absolute" top="56px" left={0} right={0} bottom={0} pointerEvents="none">
              {Array.from({ length: daysToShow }).map((_, i) => (
                <Box
                  key={i}
                  position="absolute"
                  left={`${i * 40}px`}
                  top={0}
                  bottom={0}
                  w="1px"
                  bg={isDark ? "#2E2F33" : "gray.100"}
                  opacity={isSameDay(addDays(currentWeekStart, i), new Date()) ? 1 : 0.5}
                  borderLeft={isSameDay(addDays(currentWeekStart, i), new Date()) ? "2px solid #ef4444" : "none"}
                  zIndex={isSameDay(addDays(currentWeekStart, i), new Date()) ? 5 : 0}
                />
              ))}
            </Box>

            {/* Timeline Rows */}
            <Box pt="0">
              {usersWithTasks.map((user) => {
                const userTasks = userTasksInView[user.id] || [];
                const { taskLevelMap, totalLevels } = getTaskLevelsForUser(userTasks);
                const rowHeight = Math.max(80, 10 + (totalLevels * 52));

                return (
                  <Box key={user.id} h={`${rowHeight}px`} position="relative" borderBottom="1px" borderColor={isDark ? "#2E2F33" : "gray.100"}>
                    {userTasks.map((task, idx) => {
                      try {
                        const start = parseISO(task.plannedStart);
                        const end = parseISO(task.plannedEnd);
                        const diffStart = differenceInDays(start, currentWeekStart);
                        const duration = differenceInDays(end, start) + 1;
                        const left = diffStart * 40;
                        const width = Math.max(40, duration * 40);
                        
                        const level = taskLevelMap[task.id] || 0;
                        const topPos = 8 + (level * 52);
                        const colorSet = getTaskColor(task);

                        return (
                          <Tooltip key={`${task.id}-${idx}`} label={`${task.name} (${task.groupName})`}>
                            <Box
                              position="absolute"
                              left={`${left}px`}
                              width={`${width - 4}px`}
                              top={`${topPos}px`}
                              h="48px"
                              bg={colorSet.bg}
                              borderRadius="6px"
                              px={3}
                              py={1.5}
                              cursor="pointer"
                              transition="all 0.2s"
                              _hover={{ filter: "brightness(1.05)", zIndex: 10, boxShadow: "lg" }}
                              onClick={() => setSelectedTask(task)}
                              overflow="hidden"
                              boxShadow="sm"
                              display="flex"
                              flexDirection="column"
                              justifyContent="space-between"
                            >
                              <Text fontSize="11px" fontWeight="bold" color="white" isTruncated>{task.name}</Text>
                              <Flex justify="space-between" align="center" w="full">
                                <Text fontSize="9px" fontWeight="600" color="whiteAlpha.900" isTruncated>{task.groupName}</Text>
                                <Box bg="whiteAlpha.300" px={1.5} py={0.5} borderRadius="md">
                                  <Text fontSize="9px" fontWeight="800" color="white">{task.progress}%</Text>
                                </Box>
                              </Flex>
                            </Box>
                          </Tooltip>
                        );
                      } catch (e) {
                        return null;
                      }
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Flex>
      </Box>

      {/* Task Details Modal UI */}
      <AnimatePresence>
        {selectedTask && (
          <MotionBox
            initial={{ x: 400 }}
            animate={{ x: 0 }}
            exit={{ x: 400 }}
            position="absolute"
            right="0"
            top="0"
            bottom="0"
            width="400px"
            backgroundColor={isDark ? "#1A1C1F" : "white"}
            zIndex={100}
            borderLeft={`1px solid ${isDark ? "#2E2F33" : "#E2E8F0"}`}
            overflowY="auto"
            boxShadow="xl"
          >
            <Box p={6}>
              <Flex justify="space-between" align="center" mb={6}>
                <Text fontWeight="bold" fontSize="lg" color={isDark ? "white" : "gray.800"}>Task Details</Text>
                <IconButton aria-label="Close" icon={<IconWrapper icon={FiChevronRight} size={20} />} size="sm" variant="ghost" onClick={() => setSelectedTask(null)} />
              </Flex>

              <VStack align="start" spacing={5} w="full">
                <VStack align="start" spacing={1} w="full">
                  <Text fontSize="10px" fontWeight="bold" color="gray.500" letterSpacing="wider">TASK NAME</Text>
                  <Text fontSize="md" fontWeight="bold" color={isDark ? "white" : "gray.800"}>{selectedTask.name}</Text>
                </VStack>

                <Divider borderColor={isDark ? "#2E2F33" : "gray.100"} />

                <VStack align="start" spacing={2}>
                  <Text fontSize="10px" fontWeight="bold" color="gray.500" letterSpacing="wider">STATUS</Text>
                  <Box
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    fontSize="9px"
                    fontWeight="bold"
                    color={getStatusConfig(selectedTask.status).color}
                    bg={getStatusConfig(selectedTask.status).bg}
                    border="1px solid"
                    borderColor={getStatusConfig(selectedTask.status).border}
                  >
                    {selectedTask.status?.toUpperCase()}
                  </Box>
                </VStack>

                <Divider borderColor={isDark ? "#2E2F33" : "gray.100"} />

                <Box w="full">
                  <Text fontSize="10px" fontWeight="bold" color="gray.500" mb={3} letterSpacing="wider">PROGRESS</Text>
                  <Progress value={selectedTask.progress} size="xs" borderRadius="full" colorScheme="blue" bg={isDark ? "#2E2F33" : "gray.100"} />
                  <Text fontSize="xs" textAlign="right" mt={2} fontWeight="semibold" color={isDark ? "gray.400" : "gray.600"}>{selectedTask.progress}% Complete</Text>
                </Box>

                <Divider borderColor={isDark ? "#2E2F33" : "gray.100"} />

                <Grid templateColumns="repeat(2, 1fr)" gap={4} w="full">
                  <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="bold" mb={2} letterSpacing="wider">PLANNED START</Text>
                    <Text fontSize="sm" fontWeight="semibold" color={isDark ? "gray.200" : "gray.700"}>
                      {safeDateFormat(selectedTask.plannedStart)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="bold" mb={2} letterSpacing="wider">PLANNED END</Text>
                    <Text fontSize="sm" fontWeight="semibold" color={isDark ? "gray.200" : "gray.700"}>
                      {safeDateFormat(selectedTask.plannedEnd)}
                    </Text>
                  </Box>
                </Grid>

                <Divider borderColor={isDark ? "#2E2F33" : "gray.100"} />

                <Box w="full">
                  <Text fontSize="10px" fontWeight="bold" color="gray.500" mb={3} letterSpacing="wider">OWNERS</Text>
                  <VStack align="start" spacing={3}>
                    {selectedTask.assignedUsers?.map((userId: string) => {
                      const user = users.find(u => u.id === userId);
                      return user ? (
                        <HStack key={userId} spacing={3}>
                          <Avatar size="xs" name={`${user.firstName} ${user.lastName}`} src={user.photoURL} />
                          <Text fontSize="sm" fontWeight="medium" color={isDark ? "gray.200" : "gray.700"}>
                            {user.firstName} {user.lastName}
                          </Text>
                        </HStack>
                      ) : null;
                    })}
                  </VStack>
                </Box>
              </VStack>
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: ${isDark ? "#14161B" : "#f1f1f1"};
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${isDark ? "#2E2F33" : "#888"};
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? "#3A3D44" : "#555"};
        }
      `}</style>
    </Flex>
  );
};

export default ResourceView;
